import {
  Box,
  CircularProgress,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import {
  type PaymentErrorCode,
  TransactionStatus,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { PaymentFailureMessage } from "Components/Transactions/PaymentFailureMessage";
import { transactionStatusToColor } from "Utils/TransactionUtils";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import type { ReactNode } from "react";

const ComponentResources = Resources.Merchant.InitiateDetail;

const transactionStatusToAdornment = (
  color?: string,
): Record<TransactionStatus, ReactNode> => ({
  [TransactionStatus.Created]: null,
  [TransactionStatus.Initiated]: null,
  [TransactionStatus.Fail]: null,
  [TransactionStatus.Cancelled]: null,
  [TransactionStatus.InvestigationNeeded]: null,
  [TransactionStatus.Processing]: (
    <CircularProgress size={25} thickness={5} sx={{ mt: "1px", color }} />
  ),
  [TransactionStatus.Success]: (
    <CheckCircleOutlinedIcon sx={{ height: 31, width: 31, ml: -1 }} />
  ),
});

type Props = {
  transactionStatus?: TransactionStatus;
  isLoading?: boolean;
  failureReason?: PaymentErrorCode;
};

export const PaymentStatus = ({
  transactionStatus,
  isLoading,
  failureReason,
}: Props) => {
  const { t } = useResource();

  const label = transactionStatus
    ? t(Resources.Transactions.TransactionStatus[transactionStatus])
    : "";

  // Cancelled carries a reason too (Expired from the cancel sweep) — the message belongs under both.
  const isFailed =
    transactionStatus === TransactionStatus.Fail ||
    transactionStatus === TransactionStatus.Cancelled;

  const color = transactionStatus
    ? transactionStatusToColor[transactionStatus]
    : undefined;

  const adornment = transactionStatus
    ? transactionStatusToAdornment(color)[transactionStatus]
    : null;

  return (
    <Stack gap={2}>
      <Typography fontWeight={500}>
        {t(ComponentResources.PaymentStatus)}
      </Typography>
      <Typography variant="h3" color={color}>
        <Box
          component="span"
          display="inline-flex"
          justifyContent="center"
          alignItems="center"
          gap={3}
          flexWrap="wrap"
        >
          {isLoading ? <Skeleton variant="rectangular" /> : label}
          {adornment}
        </Box>
      </Typography>
      {isFailed && failureReason !== undefined && (
        <Box mt={2}>
          <PaymentFailureMessage failureReason={failureReason} />
        </Box>
      )}
    </Stack>
  );
};
